import { createTheme } from '@mui/material/styles';

// Create a modern theme instance with vibrant colors
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#604221', // Brand color - rich brown
      light: '#8B6F47',
      dark: '#3D2B16',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#FF6D00', // Vibrant orange - energy and excitement
      light: '#FF9E40',
      dark: '#C43E00',
      contrastText: '#FFFFFF',
    },
    tertiary: {
      main: '#6200EA', // Purple accent for premium feel
      light: '#9C47FF',
      dark: '#3700B3',
      contrastText: '#FFFFFF',
    },
    background: {
      default: 'linear-gradient(135deg, #604221 0%, #8B6F47 100%)',
      paper: '#FFFFFF',
      gradient: 'linear-gradient(135deg, #604221 0%, #8B6F47 50%, #FF6D00 100%)',
      cardGradient: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
    },
    // Gradient backgrounds
    gradients: {
      primary: 'linear-gradient(135deg, #604221 0%, #FF6D00 100%)',
      secondary: 'linear-gradient(135deg, #FF6D00 0%, #6200EA 100%)',
      tertiary: 'linear-gradient(135deg, #6200EA 0%, #604221 100%)',
      brand: 'linear-gradient(135deg, #604221 0%, #8B6F47 100%)',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#666666',
      disabled: '#9E9E9E',
    },
    error: {
      main: '#FF1744',
      light: '#FF5983',
      dark: '#C4001D',
    },
    warning: {
      main: '#FF9100',
      light: '#FFC246',
      dark: '#C56200',
    },
    info: {
      main: '#00B0FF',
      light: '#40C4FF',
      dark: '#0081CB',
    },
    success: {
      main: '#604221',
      light: '#8B6F47',
      dark: '#3D2B16',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Segoe UI", "Helvetica Neue", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '3rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2.25rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.875rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.6,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 16,
  },
  spacing: 8,
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.08)',
    '0px 8px 16px rgba(0, 0, 0, 0.1)',
    '0px 12px 24px rgba(0, 0, 0, 0.12)',
    '0px 16px 32px rgba(0, 0, 0, 0.15)',
    '0px 20px 40px rgba(0, 0, 0, 0.18)',
    '0px 24px 48px rgba(0, 0, 0, 0.2)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          fontSize: '0.875rem',
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0px 8px 25px rgba(0, 0, 0, 0.15)',
          },
          '& .MuiSvgIcon-root': {
            color: 'inherit',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #604221 0%, #8B6F47 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #3D2B16 0%, #604221 100%)',
          },
          '& .MuiSvgIcon-root': {
            color: 'inherit',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #FF6D00 0%, #FF9100 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #C43E00 0%, #F57C00 100%)',
          },
          '& .MuiSvgIcon-root': {
            color: 'inherit',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: 'rgba(96, 66, 33, 0.04)',
          },
          '& .MuiSvgIcon-root': {
            color: 'inherit',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 16,
            backgroundColor: '#FAFAFA',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#F5F5F5',
            },
            '&.Mui-focused': {
              backgroundColor: '#FFFFFF',
              boxShadow: '0px 0px 0px 3px rgba(96, 66, 33, 0.1)',
            },
            '& .MuiIconButton-root': {
              color: '#604221',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 500,
        },
        colorPrimary: {
          background: 'linear-gradient(135deg, #604221 0%, #8B6F47 100%)',
          color: '#FFFFFF',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
          '& .MuiIconButton-root': {
            color: '#ffffff',
          },
          '& .MuiSvgIcon-root': {
            color: '#ffffff',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          '& .MuiListItemIcon-root': {
            color: '#604221',
          },
          '& .MuiSvgIcon-root': {
            color: '#604221',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.MuiIconButton-colorInherit': {
            color: 'inherit',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: '#F5F5F5',
        },
      },
    },
  },
});

export default theme;